// Normalise une chaîne pour comparaison insensible aux accents/casse.
// (Les apostrophes ne sont jamais comparées : elles servent uniquement de
// séparateurs de tokens, cf. maskDefinition.)
export function stripDiacritics(s) {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

// Transforme motsCaches en liste plate de termes à masquer.
export function normalizeMotsCaches(motsCaches) {
  if (!motsCaches) return [];

  const items = Array.isArray(motsCaches) ? motsCaches : [motsCaches];

  return items
    .flatMap((item) => String(item).split(/[,;]/))
    .map((m) => m.trim())
    .filter(Boolean);
}

// Extrait les « mots » normalisés (suites de lettres) d'un terme caché.
// Ex: "l'achillée" -> ["l", "achillee"] ; "à hue" -> ["a", "hue"].
//
// IMPORTANT : on normalise (stripDiacritics = NFKD + suppression des marques
// combinantes) AVANT de découper. Sinon un accent stocké en forme décomposée
// (NFD : « e » + U+0301) casserait le mot, car la marque combinante n'est pas
// une lettre \p{L} : « inféré » (NFD) deviendrait ["infe", "re"] au lieu de
// ["infere"], et ne serait jamais masqué.
function extractWords(term) {
  return Array.from(stripDiacritics(term).matchAll(/\p{L}+/gu)).map((m) => m[0]);
}

// Longueur fixe du remplacement : 10 underscores, quelle que soit la longueur
// du mot masqué, afin de ne donner aucune indication sur sa taille.
export const MASK_PLACEHOLDER = "_".repeat(10);

// Remplace les mots cachés d'une définition par un espace masqué (____).
//
// Principe : on ne modifie JAMAIS le reste du texte affiché (apostrophes,
// accents, ponctuation, espaces restent intacts) ; chaque zone masquée est
// remplacée par un placeholder de longueur fixe.
//
// Le texte est découpé en « tokens » = suites maximales de lettres. Tout
// caractère non-lettre (espace, apostrophe, trait d'union, ponctuation) est un
// séparateur. Ainsi une élision comme « l'achillée » produit deux tokens
// « l » et « achillée », ce qui permet de masquer « achillée » sans toucher à
// l'apostrophe.
export function maskDefinition(definition = "", motsCaches = []) {
  if (!definition) return "";

  const text = String(definition).normalize("NFC");
  const chars = [...text];
  const total = chars.length;

  // Découpage en tokens de lettres, avec correspondance forme normalisée -> index de caractère.
  const tokens = [];
  let i = 0;
  while (i < total) {
    if (/\p{L}/u.test(chars[i])) {
      const start = i;
      while (i < total && /\p{L}/u.test(chars[i])) i++;
      const end = i; // exclusif

      let norm = "";
      const normToChar = [];
      for (let c = start; c < end; c++) {
        const stripped = stripDiacritics(chars[c]);
        for (let k = 0; k < stripped.length; k++) {
          norm += stripped[k];
          normToChar.push(c);
        }
      }
      tokens.push({ start, end, norm, normToChar });
    } else {
      i++;
    }
  }

  const masked = new Array(total).fill(false);

  const caches = normalizeMotsCaches(motsCaches)
    .map((term) => ({ words: extractWords(term) }))
    .filter((c) => c.words.length > 0);

  // 1) Correspondance exacte d'une séquence de tokens (mot simple ou locution).
  //    On traite les termes les plus longs d'abord (sans incidence sur le résultat,
  //    mais cohérent avec l'ancien comportement).
  const bySeq = caches.slice().sort((a, b) => b.words.length - a.words.length);
  for (const cache of bySeq) {
    const target = cache.words;
    const tlen = target.length;
    for (let s = 0; s + tlen <= tokens.length; s++) {
      let match = true;
      for (let k = 0; k < tlen; k++) {
        if (tokens[s + k].norm !== target[k]) {
          match = false;
          break;
        }
      }
      if (match) {
        // On masque tout l'intervalle (séparateurs internes inclus) pour qu'une
        // locution devienne un seul bloc, sans révéler le nombre de mots.
        const spanStart = tokens[s].start;
        const spanEnd = tokens[s + tlen - 1].end;
        for (let c = spanStart; c < spanEnd; c++) masked[c] = true;
      }
    }
  }

  // 2) Masquage d'une sous-chaîne à l'intérieur d'un token, pour les termes
  //    d'un seul mot (ex: masquer « chat » dans « chats »). Aligné sur la forme
  //    normalisée puis remappé sur les caractères d'origine.
  const singles = [
    ...new Set(caches.filter((c) => c.words.length === 1).map((c) => c.words[0])),
  ].sort((a, b) => b.length - a.length);

  if (singles.length) {
    for (const t of tokens) {
      if (!t.norm) continue;
      for (const hidden of singles) {
        if (!hidden) continue;
        let from = 0;
        while (from <= t.norm.length - hidden.length) {
          const idx = t.norm.indexOf(hidden, from);
          if (idx === -1) break;
          const charStart = t.normToChar[idx];
          const charEnd = t.normToChar[idx + hidden.length - 1];
          for (let c = charStart; c <= charEnd; c++) masked[c] = true;
          from = idx + hidden.length;
        }
      }
    }
  }

  // Reconstruction : chaque zone masquée contiguë devient un placeholder de
  // longueur fixe (aucune indication sur la longueur réelle du mot).
  let out = "";
  let idx = 0;
  while (idx < total) {
    if (masked[idx]) {
      out += MASK_PLACEHOLDER;
      while (idx < total && masked[idx]) idx++;
    } else {
      out += chars[idx];
      idx++;
    }
  }
  return out;
}
